import { useAtom, useSetAtom } from "jotai";
import { useReactFlow } from "@xyflow/react";
import {
  nodesAtom,
  edgesAtom,
  zslAtom,
  canvasAtom,
} from "@zapier/canvas-state";
import { validateZSL } from "./validateZSL";
import { ComposerProvider } from "@zapier/agent-system";
import { parseCanvasDataFromZSL } from "@zapier/streaming/src/utils/zslToCanvas";
import { ComposerInitializer } from "../ComposerInitializer";
import { zodSchema } from "@ai-sdk/ui-utils";
/**
 * TEMPORARY TYPE DECLARATION
 * This is a temporary solution to fix type errors with @zapier/zsl-creator-schemas
 * This should be replaced with proper type declarations from the package in the future
 */
// @ts-ignore
import { ModifiedFinalZSLSchema } from "@zapier/zsl-creator-schemas";
import { z } from "zod";
import { useZslPieceCompletion } from "@zapier/streaming/src/hooks/useZslPieceCompletion";
import { useCallback, useRef } from "react";
import { renderZslToCanvasCommand } from "@zapier/streaming/src/atoms/renderZslToCanvasCommand";
import { outlineAtom } from "@zapier/streaming/src/atoms/outlineAtom";

// Custom hook for creating a promise-based submission handler
const useSubmitAndWait = (config: {
  api: string;
  onError?: (error: string) => void;
}) => {
  const promiseRef = useRef<{
    resolve: (value: unknown) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  const { handleSubmit } = useZslPieceCompletion({
    api: config.api,
    async onComplete(zsl) {
      if (promiseRef.current) {
        promiseRef.current.resolve(zsl);
        promiseRef.current = null;
      }
    },
    onError(error: string) {
      if (config.onError) {
        config.onError(error);
      }
      if (promiseRef.current) {
        promiseRef.current.reject(error);
        promiseRef.current = null;
      }
    },
  });

  const submitAndWait = useCallback(
    (prompt: string) => {
      return new Promise((resolve, reject) => {
        promiseRef.current = { resolve, reject };
        handleSubmit(prompt);
      });
    },
    [handleSubmit],
  );

  return submitAndWait;
};

function ComposerInitializerComponent({
  children,
}: {
  children: React.ReactNode;
}) {
  ComposerInitializer();
  return children;
}

export function CanvasComposerHydrator({
  children,
}: {
  children: React.ReactNode;
}) {
  const { fitView } = useReactFlow();
  const renderZslToCanvas = useSetAtom(renderZslToCanvasCommand);
  const setNodes = useSetAtom(nodesAtom);
  const setEdges = useSetAtom(edgesAtom);
  const [zsl, setZsl] = useAtom(zslAtom);
  const { setViewport } = useReactFlow();
  const setCanvas = useSetAtom(canvasAtom);
  const setOutline = useSetAtom(outlineAtom);
  const handleZslToCanvas = useCallback(
    async (zsl: any) => {
      try {
        setZsl(zsl);
        const nodesAndEdges = await parseCanvasDataFromZSL(zsl);
        if (nodesAndEdges) {
          const {
            canvas: { nodes, edges },
          } = nodesAndEdges;
          setNodes(nodes);
          setEdges(edges as any);
          fitView();
        }
      } catch (error) {
        console.error("Error converting ZSL to Canvas:", error);
      }
    },
    [fitView, setEdges, setNodes, setZsl],
  );

  const submitAndWait = useSubmitAndWait({
    api: "/api/zsl/v2/combined_stream",
    onError: (error: string) => console.error("Error:", error),
  });

  const resetCanvas = useCallback(() => {
    setNodes([]);
    setCanvas((prev) => ({
      ...prev,
      nodes: [],
      edges: [],
    }));
    setOutline("");
    setEdges([]);
    setZsl(undefined);
    setViewport({ x: 0, y: 0, zoom: 1 });
  }, [setNodes, setEdges, setViewport, setOutline, setZsl, setCanvas]);

  const canvasTools = {
    update_ui: {
      description: "Necessary to update the UI with the created zsl.",
      parameters: zodSchema(ModifiedFinalZSLSchema).jsonSchema,
      execute: async (args: any) => {
        await handleZslToCanvas(args);
        const zsl = args as any;
        const validationResult = await validateZSL({
          parameters: [],
          ...zsl,
        });

        if (!validationResult.valid) {
          const warningMessage =
            validationResult.errorMessage || "Validation failed";
          return {
            content: [
              {
                type: "text",
                text: `Canvas updated but validation failed.\n${warningMessage}`,
              },
            ],
          };
        } else {
          return {
            content: [{ type: "text", text: "Canvas updated" }],
          };
        }
      },
    },
    update_system: {
      description: "Create a new ZSL from the current canvas.",
      parameters: zodSchema(
        z.object({
          prompt: z
            .string()
            .describe(
              "A improved version of the users prompt to clarify users intent. This prompt will be used to update the system by another agent ",
            ),
        }),
      ).jsonSchema,
      execute: async (args: any) => {
        resetCanvas();

        // Wait for completion before returning
        await submitAndWait(args.prompt);
        await renderZslToCanvas();

        return {
          content: [{ type: "text", text: "System updated" }],
        };
      },
    },
  };

  return (
    <ComposerProvider
      orchestratorHost="canvas"
      tools={canvasTools}
      initialAdditionalData={{
        zsl,
      }}
    >
      <ComposerInitializerComponent>{children}</ComposerInitializerComponent>
    </ComposerProvider>
  );
}
